import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutData } from '@/mocks/aboutData';
import { contactFormData } from '@/mocks/contactFormData';

export default function AboutPage() {
  const { brand, reputation, contact } = aboutData;
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formUrl, setFormUrl] = useState('');

  useEffect(() => {
    setFormUrl('https://readdy.ai/api/form/d9pis6ti2vhsdh2d35eg');
  }, []);

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91官网',
    url: 'https://91guanwang.com/',
    description: '91官网是您访问91平台的官方正版入口，提供安全认证、实名保障与稳定服务。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: contact.email,
      telephone: contact.phone,
      availableLanguage: ['Chinese'],
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      return;
    }

    if (!formUrl) {
      setFormError('表单服务未就绪，请稍后再试');
      setFormStatus('error');
      return;
    }

    try {
      const formData = new FormData(form);
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          urlEncoded.append(key, value.toString());
        }
      });

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络异常，请稍后再试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-1">
                <p className="text-sm font-medium text-primary-600 mb-2">{brand.subtitle}</p>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-6 font-heading">{brand.title}</h1>
                {brand.description.map((paragraph, index) => (
                  <p key={index} className="text-base text-foreground-600 leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
              <div className="flex-shrink-0 w-full lg:w-[420px]">
                <div className="rounded-2xl overflow-hidden h-[320px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20interior%20with%20warm%20lighting%2C%20professional%20team%20workspace%2C%20clean%20aesthetic%20design%2C%20coral%20and%20amber%20accent%20colors%2C%20soft%20natural%20light%2C%20corporate%20atmosphere%2C%20no%20people&width=840&height=640&seq=about-brand-2026&orientation=landscape"
                    alt="91官网品牌形象"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100/70">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">{reputation.title}</h2>
              <p className="text-sm md:text-base text-foreground-600">多年耕耘，始终如一，用实力赢得信赖</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reputation.items.map((item) => (
                <div key={item.id} className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center hover:border-primary-200/60 transition-colors cursor-default">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2 font-heading">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">{contact.title}</h2>
              <p className="text-sm md:text-base text-foreground-600">{contact.description}</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950 mb-1 font-heading">电子邮箱</h4>
                      <p className="text-sm text-foreground-600">{contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950 mb-1 font-heading">客服电话</h4>
                      <p className="text-sm text-foreground-600">{contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950 mb-1 font-heading">工作时间</h4>
                      <p className="text-sm text-foreground-600">{contact.workingHours}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-share-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950 mb-2 font-heading">社交媒体</h4>
                      <div className="flex items-center gap-3">
                        {contact.socialLinks.map((link) => (
                          <a key={link.id} href={link.url} className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors cursor-pointer" title={link.name}>
                            <i className={`${link.icon} text-lg`}></i>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="p-6 md:p-8 rounded-2xl bg-background-100/60 border border-background-200/70">
                  <h3 className="text-lg font-bold text-foreground-950 mb-2 font-heading">{contactFormData.title}</h3>
                  <p className="text-sm text-foreground-500 mb-6">{contactFormData.description}</p>

                  {formStatus === 'success' ? (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-4">
                        <i className="ri-check-line text-2xl"></i>
                      </div>
                      <p className="text-foreground-900 font-medium">感谢您的留言！</p>
                      <p className="text-sm text-foreground-500 mt-1">我们会尽快与您联系</p>
                    </div>
                  ) : (
                    <form data-readdy-form onSubmit={handleSubmit} noValidate>
                      <input
                        type="text"
                        name="website_alt"
                        className="anti-spam-field"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />

                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="flex-1">
                            <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                              {contactFormData.fields.name.label} <span className="text-primary-500">*</span>
                            </label>
                            <input
                              id="contact-name"
                              type="text"
                              name="name"
                              required
                              placeholder={contactFormData.fields.name.placeholder}
                              className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300/50 focus:border-primary-300/50 transition-all"
                            />
                          </div>
                          <div className="flex-1">
                            <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                              {contactFormData.fields.email.label} <span className="text-primary-500">*</span>
                            </label>
                            <input
                              id="contact-email"
                              type="email"
                              name="email"
                              required
                              placeholder={contactFormData.fields.email.placeholder}
                              className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300/50 focus:border-primary-300/50 transition-all"
                            />
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="flex-1">
                            <label htmlFor="contact-phone" className="block text-xs font-medium text-foreground-700 mb-1.5">
                              {contactFormData.fields.phone.label}
                            </label>
                            <input
                              id="contact-phone"
                              type="tel"
                              name="phone"
                              placeholder={contactFormData.fields.phone.placeholder}
                              className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300/50 focus:border-primary-300/50 transition-all"
                            />
                          </div>
                          <div className="flex-1">
                            <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                              {contactFormData.fields.subject.label} <span className="text-primary-500">*</span>
                            </label>
                            <input
                              id="contact-subject"
                              type="text"
                              name="subject"
                              required
                              placeholder={contactFormData.fields.subject.placeholder}
                              className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300/50 focus:border-primary-300/50 transition-all"
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                            {contactFormData.fields.message.label} <span className="text-primary-500">*</span>
                          </label>
                          <textarea
                            id="contact-message"
                            name="message"
                            required
                            rows={4}
                            maxLength={500}
                            placeholder={contactFormData.fields.message.placeholder}
                            className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300/50 focus:border-primary-300/50 transition-all resize-none"
                          ></textarea>
                          <p className="text-xs text-foreground-400 mt-1">最多输入500个字符</p>
                        </div>

                        {formStatus === 'error' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-primary-50 text-primary-700 text-sm">
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                              <i className="ri-error-warning-line text-sm"></i>
                            </div>
                            {formError}
                          </div>
                        )}

                        <button
                          type="submit"
                          className="px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors text-sm whitespace-nowrap cursor-pointer"
                        >
                          提交留言
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      </main>
      <Footer />
    </>
  );
}